import React, { useState } from "react";
import type { Bundle } from "@/types";
import { mediaUrl } from "@/lib/api";
import { formatUzs } from "@/lib/format";
import { flyToCart } from "@/lib/motion";
import { useI18n } from "@/i18n";
import { useCart } from "@/state/CartContext";
import { useRouter } from "@/router/Router";
import { ProductImage } from "./ProductImage";
import { MainAction } from "./MainAction";

export function savingPct(b: Bundle): number {
  return b.listPriceUzs > 0 ? Math.round((1 - b.priceUzs / b.listPriceUzs) * 100) : 0;
}

/** Bundle tile for rails (home, cart). */
export function BundleCard({ bundle, onOpen, width = 240 }: { bundle: Bundle; onOpen: () => void; width?: number | string }) {
  const { t, locale } = useI18n();
  const pct = savingPct(bundle);
  return (
    <button className="bundle-card" style={{ width }} onClick={onOpen}>
      <div className="bundle-card__art">
        {bundle.image ? (
          <img src={mediaUrl(bundle.image)} alt="" className="bundle-card__cover" />
        ) : (
          bundle.items.slice(0, 3).map((i, idx) => <ProductImage key={i.variantId} src={i.image ?? undefined} alt="" className={`bundle-card__pack bundle-card__pack--${idx}`} />)
        )}
        {pct > 0 && <span className="bundle-card__save">−{pct}%</span>}
      </div>
      <div className="bundle-card__body">
        <span className="eyebrow">{t("bundles.eyebrow", { count: bundle.items.reduce((n, i) => n + i.quantity, 0) })}</span>
        <div className="bundle-card__title">{bundle.title}</div>
        <div className="bundle-card__price">
          <span className="price">{formatUzs(bundle.priceUzs, locale)}</span>
          {pct > 0 && <span className="price price--old">{formatUzs(bundle.listPriceUzs, locale)}</span>}
        </div>
      </div>
    </button>
  );
}

/** Bottom sheet with the bundle's contents and "Add set to cart". */
export function BundleSheet({ bundle, onClose }: { bundle: Bundle | null; onClose: () => void }) {
  const { t, locale } = useI18n();
  const { addBundle } = useCart();
  const { navigate } = useRouter();
  const [added, setAdded] = useState(false);
  if (!bundle) return null;
  const soldOut = bundle.available <= 0;
  const pct = savingPct(bundle);

  const add = () => {
    if (soldOut) return;
    if (added) {
      onClose();
      navigate({ name: "cart" });
      return;
    }
    flyToCart(document.querySelector(".bundle-sheet__art"));
    addBundle(bundle.id);
    setAdded(true);
  };

  return (
    <div className="sheet-overlay" onClick={onClose}>
      <div className="sheet" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="sheet__handle" />
        <div className="bundle-sheet__art">
          {bundle.image ? <img src={mediaUrl(bundle.image)} alt="" /> : bundle.items.slice(0, 3).map((i) => <ProductImage key={i.variantId} src={i.image ?? undefined} alt="" className="bundle-sheet__pack" />)}
        </div>
        <span className="eyebrow">{t("bundles.title")}</span>
        <h2 className="sheet__title">{bundle.title}</h2>
        {bundle.description && <p className="sheet__text">{bundle.description}</p>}
        <div className="bundle-sheet__items">
          {bundle.items.map((i) => (
            <div key={i.variantId} className="total-row" style={{ color: "var(--color-ink)" }}>
              <span>
                {i.productName}
                {i.variantLabel !== "Стандарт" && <span style={{ color: "var(--color-muted)" }}> · {i.variantLabel}</span>}
              </span>
              <span>× {i.quantity}</span>
            </div>
          ))}
        </div>
        <div className="total-row total-row--grand">
          <span>{pct > 0 ? t("bundles.youSave", { pct }) : t("cart.total")}</span>
          <span>
            {pct > 0 && <span className="price price--old" style={{ marginRight: 8 }}>{formatUzs(bundle.listPriceUzs, locale)}</span>}
            {formatUzs(bundle.priceUzs, locale)}
          </span>
        </div>
        <p className="hint-line">{soldOut ? t("bundles.soldOut") : t("bundles.noPromo")}</p>
        <MainAction text={soldOut ? t("bundles.soldOut") : added ? t("bundles.goToCart") : t("bundles.add")} onClick={() => add()} disabled={soldOut} hidden={!bundle} />
      </div>
    </div>
  );
}
